import { Order } from "src/app/types/orders/order";
import { useEffect, useState } from "react";
import { IActionsModal } from "src/app/types/actionsModal";
import { Row, Col, Button } from "react-bootstrap";
import { AIGuideFormStepsList } from "./AIGuideFormStepsList";
import { SimpleModal } from "src/app/components";
import { useIntl } from "react-intl";
import {
    useGetJarvisAiManualIntentSearch,
    useGetJarvisAiManualTitle,
    useGetJarvisAiManualPlan,
    useGetOrderById,
    useGetJarvisAiManualMeta,
    useGetJarvisAiManualText,
    useGetJarvisPromptForAiGuide,
    useGetJarvisAiManualPeopleAlsoAsk,
} from "src/app/hooks";
import { toast } from "react-toastify";
import { OrderTransport } from "src/app/transports/order";
import { countWord } from "src/app/utils/stringHelper";
import { peopleAlsoAskTitles } from "src/app/utils/peopleAlsoAskTitles";

export interface IAIGuideFormFormContainer extends IActionsModal<Order> {
    checkChanges?: boolean;
    isForReview?: boolean;
    checkMinimumWords?: boolean;
}

const AIGuideFormFormContainer = (props: IAIGuideFormFormContainer) => {
    const { handleClose = () => {}, entity, updateGridOnSave } = props;

    const { order } = useGetOrderById(entity?.id);

    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);

    const [title, setTitle] = useState<string | null>(null);
    const [intentSearch, setIntentSearch] = useState<string | null>(null);
    const [plan, setPlan] = useState<string | null>(null);
    const [extraPlanInformation, setExtraPlanInformation] = useState<string | null>(null);

    const [metaTitle, setMetaTitle] = useState<string | null>(null);
    const [metaDescription, setMetaDescription] = useState<string | null>(null);

    const [text, setText] = useState<string>("");
    const [peopleAlsoAsk, setPeopleAlsoAsk] = useState<string>("");
    const [peopleAlsoAskTitle, setPeopleAlsoAskTitle] = useState<string>();
    const [finalText, setFinalText] = useState<string>("");

    const { formatMessage } = useIntl();

    const {
        intentSearchSuggest,
        metaDescriptionSuggest,
        metaTitleSuggest,
        planSuggest,
        textSuggest,
        titleSuggest,
        loading: guideLoading,
        refresh,
    } = useGetJarvisPromptForAiGuide({
        orderId: entity?.id,
        intentSearch,
        title,
        plan,
        metaTitle,
        metaDescription,
        text,
    });

    // Setters
    const { setter: setterIntentSearch, loading: aiManualIntentSearchLoading } =
        useGetJarvisAiManualIntentSearch(entity?.id, false);

    const { setter: setterTitle, loading: aiManualTitleLoading } = useGetJarvisAiManualTitle(
        entity?.id,
        intentSearch || "",
        extraPlanInformation || "",
        false
    );

    const { setter: setterPlan, loading: aiManualPlanLoading } = useGetJarvisAiManualPlan(
        entity?.id,
        title || "",
        `${intentSearch} ${
            extraPlanInformation
                ? `\n\n<h2>Information important:</h2> \n\n ${extraPlanInformation}`
                : ""
        }` || "",
        false
    );

    const { setter: setterMeta, loading: aiManualMetaLoading } = useGetJarvisAiManualMeta(
        entity?.id,
        title || "",
        plan || "",
        false
    );

    const { setter: setterText, loading: aiManualTextLoading } = useGetJarvisAiManualText(
        entity?.id,
        title || "",
        plan || "",
        false
    );

    const { setter: setterPeopleAlsoAsk, loading: aiManualPeopleAlsoAskLoading } =
        useGetJarvisAiManualPeopleAlsoAsk(entity?.id);

    useEffect(() => {
        if (order) {
            setTitle(order?.content?.title || null);
            setExtraPlanInformation(order.commentForWriter || null);
        }
    }, [order]);

    function toClipboard(elementId: string) {
        const element = document.getElementById(elementId) as Node;
        let range;
        let selection;

        if (window.getSelection) {
            selection = window.getSelection();
            range = document.createRange();
            range.selectNodeContents(element);
            selection?.removeAllRanges();
            selection?.addRange(range);
            document.execCommand("copy");
            window?.getSelection()?.removeAllRanges();
            toast.success(`${formatMessage({ id: "common.action.copied" })}`);
        }
    }

    // Requests
    const requestIntentSearch = () => {
        setterIntentSearch(setIntentSearch);
    };

    const requestTitle = () => {
        setterTitle(setTitle);
    };

    const requestPlan = () => {
        setterPlan(setPlan);
    };

    const requestMeta = () => {
        setterMeta((res) => {
            setMetaTitle(res.MT);
            setMetaDescription(res.MD);
        });
    };

    const requestText = () => {
        setterText((value) => {
            setText(value);
            setFinalText((finalTextValue) => {
                if (!peopleAlsoAskTitle) {
                    return `${value} <br/> ${peopleAlsoAsk}`;
                }

                const position = finalText.indexOf(peopleAlsoAskTitle);

                if (position === -1) {
                    return `${value} <br/> ${peopleAlsoAsk}`;
                }

                return `${value} <br/> ${finalTextValue.slice(position, finalTextValue.length)}}`;
            });
        });
    };

    const requestPeopleAlsoAsk = () => {
        if (!intentSearchSuggest || !order || !order.moneySite) return;

        const initIndex = intentSearchSuggest.indexOf("people_also_ask");
        if (initIndex < 0) return;
        const lastIndex = intentSearchSuggest.indexOf("related_searches");
        const paa = intentSearchSuggest.substring(initIndex, lastIndex);

        const paaTitles =
            peopleAlsoAskTitles[order?.moneySite?.lang as keyof typeof peopleAlsoAskTitles];
        const paaTitle = paaTitles[Math.floor(Math.random() * paaTitles.length - 1)];
        setterPeopleAlsoAsk(paa, (value) => {
            const valueWithTitle = `<h2>${paaTitle}</h2><br/>${value}`;
            setPeopleAlsoAskTitle(`<h2>${paaTitle}</h2>`);
            setFinalText((finalTextValue) => {
                if (!peopleAlsoAskTitle) {
                    return `${finalTextValue} <br/> ${valueWithTitle}}`;
                }
                const position = finalText.indexOf(peopleAlsoAskTitle);

                if (position === -1) {
                    return `${finalTextValue} <br/> ${valueWithTitle}}`;
                }

                return `${finalTextValue.slice(0, position)} ${valueWithTitle}}`;
            });
            setPeopleAlsoAsk(valueWithTitle);
        });
    };

    // handlers
    const handleIntentSearch = (value: string) => {
        setIntentSearch(value);
    };

    const handleTitle = (value: string) => {
        setTitle(value);
    };

    const handlePlan = (value: string) => {
        setPlan(value);
    };

    const handleText = (value: string) => {
        setFinalText(value);
    };

    const handleMetaTitle = (value: string) => {
        setMetaTitle(value);
    };

    const handleMetaDescription = (value: string) => {
        setMetaDescription(value);
    };

    const links = order?.links || [];

    const handleFields = {
        intentSearch: {
            input: intentSearch,
            setIntentSearch: handleIntentSearch,
            requestIntentSearch,
            requestLoading: aiManualIntentSearchLoading,
        },
        title: {
            input: title,
            setTitle: handleTitle,
            requestTitle,
            requestLoading: aiManualTitleLoading,
        },
        plan: {
            input: plan,
            setPlan: handlePlan,
            requestPlan,
            requestLoading: aiManualPlanLoading,
        },
        meta: {
            input: {
                mt: metaTitle,
                md: metaDescription,
            },
            setMetaTitle: handleMetaTitle,
            setMetaDescription: handleMetaDescription,
            requestMeta,
            requestLoading: aiManualMetaLoading,
        },
        text: {
            input: finalText,
            setText: handleText,
            requestText,
            youtubeLink: order?.keywords
                ? `https://www.youtube.com/results?search_query=${order?.keywords[0].replaceAll(
                      " ",
                      "+"
                  )}`
                : null,
            requestPeopleAlsoAsk,
            peopleAlsoAsk,
            disableGoToYoutube: !(order && order.keywords && order.keywords.length > 0),
            wordCount: order?.wordCount || 0,
            links,
            requestLoading: aiManualTextLoading,
        },
        image: {
            input: null,
            setImage: () => {},
            requestImage: () => {},
            requestLoading: false,
        },
    };

    const stepsList = AIGuideFormStepsList(
        {
            intentSearchSuggest,
            metaDescriptionSuggest,
            metaTitleSuggest,
            planSuggest,
            textSuggest,
            titleSuggest,
        },
        guideLoading,
        {
            value: extraPlanInformation,
            setInfo: (value: string) => setExtraPlanInformation(value),
        },

        handleFields,
        toClipboard
    );

    const totalSteps = stepsList.length;

    const goToStep = (step: number) => {
        if (step === totalSteps) {
            handleClose();
            return;
        }
        if (step < 0) {
            return;
        }

        if (step < currentStep) {
            setCurrentStep(step);
            return;
        }

        setCurrentStep(step);

        if (step === 0 && !intentSearchSuggest) {
            refresh("INTENT_SEARCH");
        } else if (step === 1 && !titleSuggest) {
            refresh("TITLE");
        } else if (step === 2 && !planSuggest) {
            refresh("PLAN");
        } else if (step === 3 && (!metaTitleSuggest || !metaTitleSuggest)) {
            refresh("META");
        } else if (step === 4 && !textSuggest) {
            refresh("TEXT");
        }
    };

    const buttonConfirmationLabel = () => {
        if (currentStep === totalSteps - 1) {
            return formatMessage({ id: "common.action.save" });
        }
        return formatMessage({ id: "common.action.continue" });
    };
    const buttonConfirmationAction = async () => {
        if (currentStep !== totalSteps - 1) {
            goToStep(currentStep + 1);
            return;
        }
        if (
            !order ||
            !order.content ||
            !title ||
            !plan ||
            !metaTitle ||
            !metaDescription ||
            !finalText
        ) {
            toast.error(formatMessage({ id: "common.notification.error" }));
            return;
        }

        setLoading(true);
        try {
            const res = await OrderTransport.addAiManuelToContent(order.content.id, {
                title,
                metaTitle,
                metaDescription,
                wordCount: countWord(finalText),
                text: finalText,
            });
            updateGridOnSave && updateGridOnSave(res.order, false, true);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (error: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    };

    const disableConfirmation = () => {
        switch (currentStep) {
            case 0:
                return typeof intentSearch !== "string";
            case 1:
                return typeof title !== "string";
            case 2:
                return typeof plan !== "string";
            case 3:
                return typeof metaTitle !== "string" || typeof metaDescription !== "string";
            case 4:
                return typeof finalText !== "string" || finalText.length === 0;
        }
        return false;
    };

    const FooterLeftComponent = (
        <div className="d-flex w-100 justify-content-end">
            {currentStep !== 0 && <Button onClick={() => goToStep(currentStep - 1)}>back</Button>}
        </div>
    );

    return (
        <SimpleModal
            showModal={true}
            handleClose={handleClose}
            buttonConfirmationAction={buttonConfirmationAction}
            buttonConfirmationLabel={buttonConfirmationLabel()}
            disableConfirmation={disableConfirmation()}
            entityName={`${formatMessage({ id: "common.entity.AIGuide" })} -> ${
                order?.keywords && order.keywords.length > 0
                    ? `${formatMessage({ id: "common.entity.keyword" })} : ${order.keywords[0]}`
                    : ""
            }`}
            formId={""}
            loading={
                aiManualIntentSearchLoading ||
                aiManualTitleLoading ||
                aiManualPlanLoading ||
                aiManualMetaLoading ||
                aiManualTextLoading ||
                aiManualPeopleAlsoAskLoading ||
                loading
            }
            modalProps={{ fullscreen: true }}
            FooterLeftComponent={FooterLeftComponent}
        >
            <>
                <Row>
                    <Col>{stepsList[currentStep].element}</Col>
                </Row>
            </>
        </SimpleModal>
    );
};

export default AIGuideFormFormContainer;
